import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const LocaleHrefContext = createContext({ map: null, setMap: () => {} });

/**
 * Holds a per-page override for the language switcher: { [locale]: hrefOrNull }.
 *   - string  → push that asPath when user picks this locale
 *   - null    → no translation in that locale; navigate to the fallback the
 *               provider chose (e.g. case study list)
 *   - missing → default Next.js locale switch (keep asPath, swap locale prefix)
 *
 * Lives above `_app.js` so the Header (inside Layout) and the page Component
 * (inside Layout's children) can share state without prop-drilling through
 * Layout.
 */
export function LocaleHrefProvider({ children }) {
  const [map, setMap] = useState(null);
  const value = useMemo(() => ({ map, setMap }), [map]);
  return (
    <LocaleHrefContext.Provider value={value}>
      {children}
    </LocaleHrefContext.Provider>
  );
}

export function useLocaleHrefMap() {
  return useContext(LocaleHrefContext).map;
}

/**
 * Register a locale → href map for the current page. Cleared automatically
 * when the page unmounts so other pages get the default behavior.
 */
export function useRegisterLocaleHref(map) {
  const { setMap } = useContext(LocaleHrefContext);
  const stableMap = useMemo(() => map, [JSON.stringify(map || null)]);
  useEffect(() => {
    setMap(stableMap || null);
    return () => setMap(null);
  }, [stableMap, setMap]);
}
